import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../api/api';
import { SupplierPaymentAvailableReceipt, SupplierPaymentBatch, SupplierPaymentBatchReadiness, SupplierPaymentPlanSuggestion } from '../../types';

const money = (value: number) => `${value.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} грн`;
const today = () => new Date().toISOString().slice(0, 10);
const th: React.CSSProperties = { padding: 8, textAlign: 'left', background: 'var(--crm-surface-alt)', color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-border)' };
const td: React.CSSProperties = { padding: 8, borderBottom: '1px solid var(--crm-table-row-border)', verticalAlign: 'top' };
type ButtonTone = 'success' | 'info' | 'danger' | 'warning' | 'muted' | 'accent';
const button = (tone: ButtonTone, extra: React.CSSProperties = {}): React.CSSProperties => ({
  background: tone === 'muted' ? 'var(--crm-surface-alt)' : `var(--crm-${tone === 'accent' ? 'accent' : `${tone}-bg`})`,
  color: tone === 'muted' ? 'var(--crm-text-muted)' : tone === 'accent' ? 'var(--crm-accent-contrast)' : `var(--crm-${tone})`,
  border: `1px solid var(--crm-${tone === 'muted' ? 'border' : tone})`,
  borderRadius: 'var(--crm-radius-md)',
  padding: '7px 12px',
  cursor: 'pointer',
  ...extra,
});
const fieldStyle: React.CSSProperties = {
  display: 'block',
  padding: 6,
  color: 'var(--crm-text)',
  background: 'var(--crm-input-bg)',
  border: '1px solid var(--crm-border)',
  borderRadius: 'var(--crm-radius-md)',
};

const statusLabel: Record<SupplierPaymentBatch['status'], string> = {
  Draft: 'Чернетка',
  Approved: 'Погоджено',
  Executed: 'Виконано',
  Cancelled: 'Скасовано',
};

const SupplierPaymentBatchesPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const linkedBatchId = searchParams.get('batchId');
  const [navigationError, setNavigationError] = useState('');
  const [receipts, setReceipts] = useState<SupplierPaymentAvailableReceipt[]>([]);
  const [batches, setBatches] = useState<SupplierPaymentBatch[]>([]);
  const [selected, setSelected] = useState<Record<number, string>>({});
  const [scheduledDate, setScheduledDate] = useState(today());
  const [notes, setNotes] = useState('');
  const [paymentDate, setPaymentDate] = useState(today());
  const [method, setMethod] = useState('Банківський переказ');
  const [reference, setReference] = useState('');
  const [editingBatch, setEditingBatch] = useState<SupplierPaymentBatch | null>(null);
  const [planningBudget, setPlanningBudget] = useState('');
  const [planningSupplierId, setPlanningSupplierId] = useState('');
  const [includeFuture, setIncludeFuture] = useState(false);
  const [paymentDetailsFilter, setPaymentDetailsFilter] = useState<'all' | 'ready' | 'missing'>('all');
  const [suggestionNote, setSuggestionNote] = useState('');
  const [readinessByBatch, setReadinessByBatch] = useState<Record<number, SupplierPaymentBatchReadiness>>({});
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [receiptResponse, batchResponse] = await Promise.all([
        api.get<SupplierPaymentAvailableReceipt[]>('/supplierpaymentbatches/available-receipts'),
        api.get<SupplierPaymentBatch[]>('/supplierpaymentbatches'),
      ]);
      setReceipts(receiptResponse.data);
      setBatches(batchResponse.data);
      setLoaded(true);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Не вдалося завантажити платіжні реєстри.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    setNavigationError('');
    if (linkedBatchId === null) return;
    if (!/^[1-9]\d*$/.test(linkedBatchId) || !Number.isSafeInteger(Number(linkedBatchId))) {
      setNavigationError('Некоректний ID реєстру у посиланні.');
      return;
    }
    if (!loaded || loading || error) return;
    if (!batches.some(batch => batch.id === Number(linkedBatchId))) {
      setNavigationError('Реєстр із посилання не знайдено або недоступний.');
      return;
    }
    document.getElementById(`payment-batch-${linkedBatchId}`)?.scrollIntoView?.({ block: 'center' });
  }, [linkedBatchId, batches, loaded, loading, error]);

  const selectedTotal = useMemo(() => Object.values(selected)
    .reduce((sum, value) => sum + (Number(value.replace(',', '.')) || 0), 0), [selected]);
  const supplierOptions = useMemo(() => Array.from(new Map(
    receipts
      .filter(receipt => receipt.supplierId !== null)
      .map(receipt => [receipt.supplierId as number, receipt.supplierName]),
  ).entries()).sort((left, right) => left[1].localeCompare(right[1], 'uk')), [receipts]);
  const filteredReceipts = useMemo(() => receipts.filter(receipt =>
    paymentDetailsFilter === 'all' ||
    (paymentDetailsFilter === 'ready' ? receipt.hasPaymentDetails : !receipt.hasPaymentDetails)),
  [receipts, paymentDetailsFilter]);
  const blockedAmount = useMemo(() => receipts
    .filter(receipt => !receipt.hasPaymentDetails)
    .reduce((sum, receipt) => sum + receipt.availableToPlan, 0), [receipts]);

  const toggleReceipt = (receipt: SupplierPaymentAvailableReceipt) => {
    setSelected(current => {
      const next = { ...current };
      if (next[receipt.inventoryReceiptId] !== undefined) delete next[receipt.inventoryReceiptId];
      else next[receipt.inventoryReceiptId] = receipt.availableToPlan.toFixed(2);
      return next;
    });
  };

  const suggestPlan = async () => {
    const budget = Number(planningBudget.replace(',', '.'));
    if (!Number.isFinite(budget) || budget <= 0) {
      setError('Вкажіть коректний бюджет для автопідбору.');
      return;
    }
    setError('');
    try {
      const response = await api.get<SupplierPaymentPlanSuggestion>('/supplierpaymentbatches/suggest-plan', {
        params: {
          scheduledDate,
          budget,
          supplierId: planningSupplierId || undefined,
          includeFuture,
        },
      });
      setSelected(Object.fromEntries(response.data.items.map(item => [
        item.inventoryReceiptId,
        item.suggestedAmount.toFixed(2),
      ])));
      setSuggestionNote(
        response.data.itemCount === 0
          ? 'Немає доступних накладних за вибраними умовами.'
          : `Підібрано ${response.data.itemCount} накладних на ${money(response.data.suggestedTotal)}. Невикористаний бюджет: ${money(response.data.remainingBudget)}.`,
      );
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Не вдалося підібрати накладні.');
    }
  };

  const exportBatch = async (batch: SupplierPaymentBatch) => {
    let url: string | null = null;
    try {
      const response = await api.get(`/supplierpaymentbatches/${batch.id}/export-excel`, { responseType: 'blob' });
      url = window.URL.createObjectURL(new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${batch.batchNumber}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Не вдалося експортувати реєстр.');
    } finally {
      if (url) window.URL.revokeObjectURL(url);
    }
  };

  const createBatch = async () => {
    const items = Object.entries(selected).map(([receiptId, value]) => ({
      inventoryReceiptId: Number(receiptId),
      amount: Number(value.replace(',', '.')),
    }));
    if (items.length === 0 || items.some(item => !Number.isFinite(item.amount) || item.amount <= 0)) {
      setError('Виберіть накладні та вкажіть коректні суми.');
      return;
    }
    try {
      const body = {
        scheduledDate: `${scheduledDate}T12:00:00`,
        notes,
        items,
      };
      if (editingBatch) {
        await api.put(`/supplierpaymentbatches/${editingBatch.id}`, body, {
          headers: { 'If-Match': `"${editingBatch.version}"` },
        });
      } else {
        await api.post('/supplierpaymentbatches', body);
      }
      setSelected({});
      setNotes('');
      setEditingBatch(null);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Не вдалося створити реєстр.');
    }
  };

  const editBatch = (batch: SupplierPaymentBatch) => {
    setEditingBatch(batch);
    setScheduledDate(batch.scheduledDate.slice(0, 10));
    setNotes(batch.notes);
    setSelected(Object.fromEntries(batch.items.map(item => [item.inventoryReceiptId, item.amount.toFixed(2)])));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEditing = () => {
    setEditingBatch(null);
    setSelected({});
    setNotes('');
    setScheduledDate(today());
  };

  const checkReadiness = async (batch: SupplierPaymentBatch) => {
    try {
      const response = await api.get<SupplierPaymentBatchReadiness>(`/supplierpaymentbatches/${batch.id}/readiness`);
      setReadinessByBatch(current => ({ ...current, [batch.id]: response.data }));
      return response.data;
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Не вдалося перевірити готовність реєстру.');
      return null;
    }
  };

  const transition = async (batch: SupplierPaymentBatch, action: 'approve' | 'cancel' | 'execute') => {
    if (action === 'execute') {
      const readiness = await checkReadiness(batch);
      if (!readiness?.canExecute) {
        setError(readiness?.blockingReason || 'Реєстр не готовий до виконання.');
        return;
      }
    }
    if (!window.confirm(action === 'execute'
      ? `Виконати реєстр ${batch.batchNumber} на ${money(batch.totalAmount)}?`
      : `${action === 'approve' ? 'Погодити' : 'Скасувати'} реєстр ${batch.batchNumber}?`)) return;
    try {
      const body = action === 'execute'
        ? { paymentDate: `${paymentDate}T12:00:00`, method, reference, notes: '' }
        : {};
      await api.post(`/supplierpaymentbatches/${batch.id}/${action}`, body, {
        headers: { 'If-Match': `"${batch.version}"` },
      });
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Не вдалося змінити статус реєстру.');
      if (e?.response?.data?.readiness) {
        setReadinessByBatch(current => ({ ...current, [batch.id]: e.response.data.readiness }));
      }
      await load();
    }
  };

  return (
    <div className="supplier-payment-batches-page" style={{ padding: 24, maxWidth: 1500, margin: '0 auto' }}>
    {navigationError && <p role="alert" style={{ color: 'var(--crm-danger)' }}>{navigationError}</p>}
      <h2 style={{ marginTop: 0, color: 'var(--crm-text)' }}>Платіжні реєстри постачальникам</h2>
      <p style={{ color: 'var(--crm-text-muted)' }}>Підготуйте перелік оплат, погодьте його та виконайте одним атомарним проведенням.</p>
      {error && <div role="alert" style={{ background: 'var(--crm-danger-bg)', color: 'var(--crm-danger)', border: '1px solid var(--crm-danger)', padding: 10, borderRadius: 6, marginBottom: 12 }}>{error}</div>}

      <section style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', padding: 16, borderRadius: 8, boxShadow: 'var(--crm-shadow-sm)', marginBottom: 20 }}>
        <h3 style={{ marginTop: 0 }}>{editingBatch ? `Редагування ${editingBatch.batchNumber}` : 'Новий реєстр'}</h3>
        {blockedAmount > 0 && (
          <div style={{ background: 'var(--crm-danger-bg)', border: '1px solid var(--crm-danger)', color: 'var(--crm-danger)', borderRadius: 6, padding: 10, marginBottom: 12 }}>
            Через відсутні платіжні реквізити заблоковано {money(blockedAmount)} у {receipts.filter(receipt => !receipt.hasPaymentDetails).length} накладних.
          </div>
        )}
        <div className="supplier-payment-batches-planning" style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'end', padding: 10, background: 'var(--crm-surface-alt)', borderRadius: 6, marginBottom: 12 }}>
          <label>Бюджет<input value={planningBudget} onChange={event => setPlanningBudget(event.target.value)} placeholder="0,00" style={{ ...fieldStyle, width: 130 }} /></label>
          <label>Постачальник
            <select value={planningSupplierId} onChange={event => setPlanningSupplierId(event.target.value)} style={{ ...fieldStyle, minWidth: 220 }}>
              <option value="">Усі постачальники</option>
              {supplierOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          </label>
          <label style={{ paddingBottom: 7 }}><input type="checkbox" checked={includeFuture} onChange={event => setIncludeFuture(event.target.checked)} /> Включати накладні після дати платежу</label>
          <button onClick={suggestPlan} style={button('success')}>Автопідбір за бюджетом</button>
          {suggestionNote && <span style={{ color: 'var(--crm-text-muted)', fontSize: 13 }}>{suggestionNote}</span>}
        </div>
        <div className="supplier-payment-batches-schedule" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'end', marginBottom: 12 }}>
          <label>Запланована дата<input type="date" value={scheduledDate} onChange={event => setScheduledDate(event.target.value)} style={fieldStyle} /></label>
          <label style={{ flex: '1 1 300px' }}>Примітка<input value={notes} onChange={event => setNotes(event.target.value)} style={{ ...fieldStyle, width: '100%' }} /></label>
          <strong>Разом: {money(selectedTotal)}</strong>
          <button onClick={createBatch} disabled={Object.keys(selected).length === 0} style={button('info', { opacity: Object.keys(selected).length === 0 ? .5 : 1 })}>{editingBatch ? 'Зберегти зміни' : 'Створити реєстр'}</button>
          {editingBatch && <button onClick={cancelEditing} style={button('muted')}>Не редагувати</button>}
          <label>Фільтр реквізитів
            <select value={paymentDetailsFilter} onChange={event => setPaymentDetailsFilter(event.target.value as 'all' | 'ready' | 'missing')} style={fieldStyle}>
              <option value="all">Усі накладні</option>
              <option value="missing">Без реквізитів</option>
              <option value="ready">Готові до оплати</option>
            </select>
          </label>
        </div>
        <div style={{ overflowX: 'auto', maxHeight: 390 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th style={th}></th><th style={th}>Термін</th><th style={th}>Постачальник</th><th style={th}>Накладна</th><th style={{ ...th, textAlign: 'right' }}>Залишок</th><th style={{ ...th, textAlign: 'right' }}>Погоджено</th><th style={{ ...th, textAlign: 'right' }}>Доступно</th><th style={{ ...th, textAlign: 'right' }}>До реєстру</th></tr></thead>
            <tbody>
              {filteredReceipts.map(receipt => {
                const checked = selected[receipt.inventoryReceiptId] !== undefined;
                return <tr key={receipt.inventoryReceiptId} style={{ background: receipt.isOverdue ? 'var(--crm-danger-bg)' : undefined }}>
                  <td style={td}><input type="checkbox" checked={checked} onChange={() => toggleReceipt(receipt)} /></td>
                  <td style={{ ...td, color: receipt.isOverdue ? 'var(--crm-danger)' : undefined }}>{new Date(receipt.paymentDueDate).toLocaleDateString('uk-UA')}</td>
                  <td style={td}>
                    {receipt.supplierName}
                    {!receipt.hasPaymentDetails && <div style={{ color: 'var(--crm-danger)', fontSize: 11 }}>{receipt.paymentDetailsIssue}</div>}
                  </td>
                  <td style={td}>{receipt.invoiceNumber || `#${receipt.inventoryReceiptId}`}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{money(receipt.outstandingAmount)}</td>
                  <td style={{ ...td, textAlign: 'right', color: receipt.approvedReservedAmount > 0 ? 'var(--crm-info)' : 'var(--crm-text-muted)' }}>{money(receipt.approvedReservedAmount)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700 }}>{money(receipt.availableToPlan)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    {checked && <input value={selected[receipt.inventoryReceiptId]} onChange={event => setSelected(current => ({ ...current, [receipt.inventoryReceiptId]: event.target.value }))} style={{ ...fieldStyle, width: 115, padding: 5, textAlign: 'right' }} />}
                  </td>
                </tr>;
              })}
              {filteredReceipts.length === 0 && <tr><td colSpan={8} style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>{loading ? 'Завантаження...' : 'Немає доступних до планування накладних за фільтром'}</td></tr>}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <div style={{ display: 'flex', gap: 12, alignItems: 'end', flexWrap: 'wrap', marginBottom: 10 }}>
          <h3 style={{ margin: 0, flex: '1 1 auto' }}>Історія реєстрів</h3>
          <label>Дата виконання<input type="date" value={paymentDate} onChange={event => setPaymentDate(event.target.value)} style={{ ...fieldStyle, padding: 5 }} /></label>
          <label>Спосіб<input value={method} onChange={event => setMethod(event.target.value)} style={{ ...fieldStyle, padding: 5 }} /></label>
          <label>Референс банку<input value={reference} onChange={event => setReference(event.target.value)} style={{ ...fieldStyle, padding: 5 }} /></label>
          <button onClick={load} style={button('muted')}>Оновити</button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--crm-surface)', color: 'var(--crm-text)' }}>
            <thead><tr><th style={th}>Реєстр</th><th style={th}>Дата</th><th style={th}>Статус</th><th style={{ ...th, textAlign: 'right' }}>Сума</th><th style={th}>Накладні</th><th style={th}>Аудит</th><th style={th}></th></tr></thead>
            <tbody>
              {batches.map(batch => <React.Fragment key={batch.id}><tr id={`payment-batch-${batch.id}`}
                style={Number(linkedBatchId) === batch.id ? { outline: '2px solid var(--crm-accent)', outlineOffset: -2 } : undefined}>
                <td style={td}><strong>{batch.batchNumber}</strong>{batch.notes && <div style={{ color: 'var(--crm-text-muted)', fontSize: 12 }}>{batch.notes}</div>}</td>
                <td style={td}>{new Date(batch.scheduledDate).toLocaleDateString('uk-UA')}</td>
                <td style={td}><strong>{statusLabel[batch.status]}</strong></td>
                <td style={{ ...td, textAlign: 'right' }}>{money(batch.totalAmount)}</td>
                <td style={td}>{batch.items.map(item => <div key={item.id}>
                  {item.supplierName} · {item.invoiceNumber || `#${item.inventoryReceiptId}`} · {money(item.amount)}
                  {(!item.supplierIban || !item.supplierTaxId || !item.supplierLegalName) && <span style={{ color: 'var(--crm-danger)' }}> · немає платіжних реквізитів</span>}
                </div>)}</td>
                <td style={td}>
                  <div>Створив: {batch.createdByUsername || 'Система'}</div>
                  {batch.approvedAtUtc && <div>Погодив: {batch.approvedByUsername || 'Система'}</div>}
                  {batch.executedAtUtc && <div>Виконав: {batch.executedByUsername || 'Система'}</div>}
                </td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {batch.status === 'Draft' && <button onClick={() => transition(batch, 'approve')} style={button('warning')}>Погодити</button>}
                    {batch.status === 'Draft' && <button onClick={() => editBatch(batch)} style={button('accent')}>Редагувати</button>}
                    {batch.status === 'Approved' && <button onClick={() => transition(batch, 'execute')} style={button('success')}>Виконати</button>}
                    {batch.status === 'Approved' && <button onClick={() => checkReadiness(batch)} style={button('muted')}>Перевірити</button>}
                    <button onClick={() => exportBatch(batch)} style={button('info')}>Excel</button>
                    {(batch.status === 'Draft' || batch.status === 'Approved') && <button onClick={() => transition(batch, 'cancel')} style={button('danger')}>Скасувати</button>}
                  </div>
                </td>
              </tr>
                {readinessByBatch[batch.id] && <tr>
                  <td colSpan={7} style={{ ...td, background: readinessByBatch[batch.id].canExecute ? 'var(--crm-success-bg)' : 'var(--crm-danger-bg)' }}>
                    <strong>{readinessByBatch[batch.id].canExecute ? 'Готовий до виконання' : readinessByBatch[batch.id].blockingReason}</strong>
                    <span style={{ marginLeft: 12 }}>
                      План: {money(readinessByBatch[batch.id].plannedAmount)} · актуальний борг: {money(readinessByBatch[batch.id].currentOutstandingAmount)}
                      {readinessByBatch[batch.id].shortfallAmount > 0 && ` · дефіцит: ${money(readinessByBatch[batch.id].shortfallAmount)}`}
                    </span>
                    {readinessByBatch[batch.id].items.filter(item => !item.isReady).map(item =>
                      <div key={item.inventoryReceiptId} style={{ color: 'var(--crm-danger)', marginTop: 4 }}>
                        {item.supplierName} · {item.invoiceNumber}: план {money(item.plannedAmount)}, доступно {money(item.currentOutstandingAmount)} — {item.issue}
                      </div>)}
                  </td>
                </tr>}
              </React.Fragment>)}
              {batches.length === 0 && <tr><td colSpan={7} style={{ ...td, textAlign: 'center', color: 'var(--crm-text-muted)' }}>{loading ? 'Завантаження...' : 'Реєстрів ще немає'}</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default SupplierPaymentBatchesPage;
